import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  Search,
  Phone,
  ArrowRight,
  Heart,
  Users,
  Clock,
  DollarSign,
  CheckCircle,
  Calendar,
  Building2,
  Briefcase,
  GraduationCap,
  Calculator,
  MapPin
} from "lucide-react";
import { Header, Footer } from "../../../components/Layout";

const AmbulatorioPage = () => {
  const benefits = [
    { 
      title: "Mantén tu vida cotidiana", 
      description: "Sigue trabajando, estudiando y con tu familia mientras recibes tratamiento",
      icon: Briefcase
    },
    { 
      title: "Menor costo", 
      description: "Significativamente más económico que la internación, sin gastos de alojamiento",
      icon: DollarSign
    },
    { 
      title: "Aplicación práctica inmediata", 
      description: "Practica lo aprendido en tu entorno real desde el primer día",
      icon: GraduationCap
    },
    { 
      title: "Red de apoyo natural", 
      description: "Mantén contacto con familia y amigos que te apoyan en el proceso",
      icon: Users
    },
    { 
      title: "Flexibilidad de horarios", 
      description: "Sesiones adaptadas a tu agenda laboral y familiar",
      icon: Calendar
    },
    { 
      title: "Transición gradual", 
      description: "Ajuste progresivo de la intensidad según tu avance",
      icon: ArrowRight
    }
  ];

  const programStructure = [
    { phase: "Fase 1: Evaluación", duration: "1-2 semanas", description: "Diagnóstico completo, plan de tratamiento personalizado" },
    { phase: "Fase 2: Intensiva", duration: "1-2 meses", description: "3-5 sesiones semanales, terapia individual y grupal" },
    { phase: "Fase 3: Consolidación", duration: "2-3 meses", description: "2-3 sesiones semanales, trabajo en prevención de recaídas" },
    { phase: "Fase 4: Seguimiento", duration: "3-6 meses", description: "1 sesión semanal o quincenal, mantenimiento de logros" }
  ];

  const idealFor = [
    "Adicciones leves a moderadas",
    "Personas con responsabilidades laborales o familiares",
    "Quienes tienen un entorno estable de apoyo",
    "Primera experiencia en tratamiento",
    "Post-internación como continuidad",
    "Adicciones conductuales (juego, tecnología)"
  ];

  const notIdealFor = [
    "Adicciones severas a sustancias duras",
    "Entornos de alto riesgo de consumo",
    "Comorbilidades psiquiátricas graves",
    "Historial de múltiples recaídas",
    "Necesidad de desintoxicación supervisada"
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "name": "Tratamiento Ambulatorio para Adicciones",
    "description": "Tratamiento de adicciones sin internación que permite mantener la vida cotidiana mientras se recibe terapia profesional.",
    "relevantSpecialty": {
      "@type": "MedicalSpecialty",
      "name": "Psiquiatría de Adicciones"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Tratamientos", "item": "https://sinadicciones.org/tratamientos" },
      { "@type": "ListItem", "position": 3, "name": "Ambulatorio", "item": "https://sinadicciones.org/tratamiento/ambulatorio" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Tratamiento Ambulatorio para Adicciones: Sin Internación | Sin Adicciones Chile</title>
        <meta name="description" content="Tratamiento ambulatorio para adicciones en Chile. Terapia sin internación que permite mantener trabajo y familia. Precios desde $300.000/mes. Conoce beneficios y programa." />
        <meta property="og:title" content="Tratamiento Ambulatorio para Adicciones" />
        <meta property="og:description" content="Recuperación sin internación. Mantén tu vida mientras superas la adicción con terapia profesional 2-3 veces por semana." />
        <link rel="canonical" href="https://sinadicciones.org/tratamiento/ambulatorio" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/tratamiento/ambulatorio" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamientos" className="hover:text-emerald-400 transition-colors">
              Tratamientos
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Ambulatorio</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center">
                <Building2 className="w-7 h-7 text-blue-400" />
              </div>
              <div className="px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full">
                <span className="text-blue-400 font-medium">Sin internación</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Tratamiento{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Ambulatorio
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              Recuperación efectiva sin dejar tu vida en pausa. 
              Recibe terapia profesional 2-3 veces por semana mientras mantienes 
              tu trabajo, familia y rutina diaria.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <DollarSign className="w-5 h-5 text-blue-400 mb-2" />
                <p className="text-white font-bold">$300.000 - $800.000</p>
                <p className="text-slate-400 text-sm">por mes</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Clock className="w-5 h-5 text-blue-400 mb-2" />
                <p className="text-white font-bold">3-6 meses</p>
                <p className="text-slate-400 text-sm">duración típica</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Calendar className="w-5 h-5 text-blue-400 mb-2" />
                <p className="text-white font-bold">2-3 sesiones</p>
                <p className="text-slate-400 text-sm">por semana</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Heart className="w-5 h-5 text-blue-400 mb-2" />
                <p className="text-white font-bold">60-70%</p>
                <p className="text-slate-400 text-sm">efectividad</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile?modality=ambulatorio"
                className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Buscar Centros Ambulatorios
              </Link>
              <Link 
                to="/calculadora"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Calcular Costo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Beneficios del Tratamiento Ambulatorio
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Una opción que equilibra la intensidad terapéutica con la continuidad de tu vida.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-blue-500/20 hover:border-blue-500/40 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Structure */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Estructura del Programa
            </h2>
            <p className="text-slate-400">
              Un tratamiento progresivo que se adapta a tu evolución.
            </p>
          </div>

          <div className="space-y-4">
            {programStructure.map((phase, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50"
              >
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-400 font-bold">{index + 1}</span>
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-white">{phase.phase}</h3>
                    <span className="px-3 py-1 bg-blue-500/10 text-blue-400 text-sm rounded-full">
                      {phase.duration}
                    </span>
                  </div>
                  <p className="text-slate-400">{phase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For / Not Ideal For */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
            ¿Es para ti?
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-emerald-500/10 rounded-2xl border border-emerald-500/30">
              <h3 className="text-xl font-bold text-emerald-400 mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6" />
                Ideal si...
              </h3>
              <ul className="space-y-3">
                {idealFor.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 bg-red-500/10 rounded-2xl border border-red-500/30">
              <h3 className="text-xl font-bold text-red-400 mb-4 flex items-center gap-2">
                <ArrowRight className="w-6 h-6" />
                Considera residencial si...
              </h3>
              <ul className="space-y-3">
                {notIdealFor.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-300">
                    <ArrowRight className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/tratamiento/residencial"
                className="mt-4 inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors"
              >
                Ver tratamiento residencial
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900/50 to-slate-800/50 border border-blue-500/20 p-8 md:p-12 text-center">
            <Building2 className="w-16 h-16 text-blue-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Encuentra tu Centro Ambulatorio
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Compara centros con programas ambulatorios cerca de ti. 
              Revisa precios, horarios y modalidades de terapia.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile?modality=ambulatorio"
                className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-500/25 flex items-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                Ver Centros Ambulatorios
              </Link>
              <Link 
                to="/asesoria"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Asesoría Experta
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Otros Tipos de Tratamiento</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Residencial", path: "/tratamiento/residencial", icon: "🏥" },
              { title: "Online", path: "/tratamiento/online", icon: "💻" },
              { title: "Desintoxicación", path: "/tratamiento/desintoxicacion", icon: "🏨" },
              { title: "Grupos de Apoyo", path: "/tratamiento/grupos-apoyo", icon: "👥" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-blue-500/30 transition-all group"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-white group-hover:text-blue-400 transition-colors">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AmbulatorioPage;
